import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { collageApi } from '../../api/client';
import type { Collage } from '../../types';
import { resolveLayout } from '../../utils/collageLayouts';
import { resolveTheme } from '../../utils/collageThemes';
import { renderCollageCanvas } from '../../utils/renderCollageCanvas';
import { resolveSafeAssetBlob } from '../../utils/loadImageSafely';
import { isNative } from '../../capacitorInit';

export function CollageViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [collage, setCollage] = useState<Collage | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    collageApi.get(id).then(setCollage).finally(() => setLoading(false));
  }, [id]);

  useEffect(load, [load]);

  async function handleShare() {
    if (!collage) return;
    setBusy(true);
    const { publicUrl } = await collageApi.publish(collage.id);
    setShareUrl(publicUrl);
    setCollage({ ...collage, isPublic: true });
    setBusy(false);
  }

  async function handleUnshare() {
    if (!collage) return;
    setBusy(true);
    await collageApi.unpublish(collage.id);
    setShareUrl(null);
    setCollage({ ...collage, isPublic: false });
    setBusy(false);
  }

  // Web-only: a plain <a download> silently does nothing useful inside a
  // Capacitor WebView (no browser download manager exists there), so this
  // is deliberately not shown on native — see the plan's cross-platform
  // section. Native users get the Share link instead; a real "save to
  // Photos" flow is an explicit fast-follow requiring new Capacitor plugins.
  async function handleDownload() {
    if (!collage?.renderedImageUrl) return;
    setBusy(true);
    try {
      const blob = await resolveSafeAssetBlob(collage.renderedImageUrl);
      const objectUrl = URL.createObjectURL(blob);
      const fileName = `${(collage.title || 'collage').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`;
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!collage) return;
    if (!window.confirm('Delete this collage? The photos it uses stay in your archive.')) return;
    setBusy(true);
    await collageApi.remove(collage.id);
    navigate('/events/collages');
  }

  async function handleRenderNow() {
    if (!collage) return;
    setRendering(true);
    try {
      const layout = resolveLayout(collage.layoutId);
      const theme = resolveTheme(collage.themeId);
      const items = [...collage.items]
        .sort((a, b) => a.position - b.position)
        .map(item => ({ slotIndex: item.position, photoUrl: item.eventPhoto.filePath, caption: item.caption }));
      const result = await renderCollageCanvas({
        layout, theme, items,
        textLayers: collage.textLayers.map(l => ({ text: l.text, xPct: l.xPct, yPct: l.yPct, size: l.size, color: l.color })),
        title: collage.title,
      });
      const { renderedImageUrl } = await collageApi.uploadRender(collage.id, result.blob);
      setCollage({ ...collage, renderedImageUrl });
    } finally {
      setRendering(false);
    }
  }

  if (loading) return <p className="text-slate-500">Loading…</p>;
  if (!collage) return <p className="text-slate-500">Collage not found.</p>;

  const theme = resolveTheme(collage.themeId);
  const sortedItems = [...collage.items].sort((a, b) => a.position - b.position);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{collage.title || 'Untitled Collage'}</h1>
          <p className="text-xs text-slate-500 mt-0.5">{collage.type === 'grid' ? 'Grid' : 'Storyboard'} &middot; {theme.name}</p>
        </div>
        <Link to="/events/collages" className="text-sm text-slate-500 hover:text-slate-700">← My Collages</Link>
      </div>

      {collage.type === 'grid' ? (
        collage.renderedImageUrl ? (
          <img src={collage.renderedImageUrl} alt={collage.title ?? 'Collage'} className="w-full rounded-xl shadow-lg mb-4" />
        ) : (
          <div className="rounded-xl border border-dashed border-indigo-200 bg-white p-8 text-center mb-4">
            <p className="text-slate-500 text-sm mb-3">This collage hasn't been rendered into an image yet.</p>
            <button
              type="button"
              disabled={rendering}
              onClick={handleRenderNow}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
            >
              {rendering ? 'Rendering…' : 'Render now'}
            </button>
          </div>
        )
      ) : (
        <div className="flex flex-col gap-3 bg-white rounded-xl border border-indigo-50 p-4 mb-4">
          {sortedItems.map(item => (
            <div key={item.id} className="flex gap-3">
              <img
                src={item.eventPhoto.filePath} alt="" loading="lazy" className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                style={{ filter: theme.previewFilterCss, objectPosition: `${item.focalX}% ${item.focalY}%` }}
              />
              <div>
                <p className="text-[11px] font-bold text-indigo-600">
                  {item.eventPhoto.takenAt ? new Date(item.eventPhoto.takenAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'Undated'}
                </p>
                {item.caption && <p className="text-sm text-slate-700">{item.caption}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {shareUrl && (
        <div className="mb-4 flex items-center gap-1.5">
          <input readOnly value={shareUrl} className="flex-1 min-w-0 text-xs border border-emerald-200 rounded-lg px-2 py-1.5 bg-emerald-50" />
          <button type="button" onClick={() => navigator.clipboard?.writeText(shareUrl)} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 flex-shrink-0">Copy</button>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <Link to={`/events/collages/${collage.id}/edit`} className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-200 transition-colors">✏️ Edit</Link>
        {collage.isPublic ? (
          <button type="button" disabled={busy} onClick={handleUnshare} className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-200 transition-colors disabled:opacity-50">🔗 Unshare</button>
        ) : (
          <button type="button" disabled={busy} onClick={handleShare} className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-200 transition-colors disabled:opacity-50">🔗 Share</button>
        )}
        {!isNative && collage.type === 'grid' && collage.renderedImageUrl && (
          <button type="button" disabled={busy} onClick={handleDownload} className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-200 transition-colors disabled:opacity-50">⬇️ Download</button>
        )}
        <button type="button" disabled={busy} onClick={handleDelete} className="px-3 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-100 transition-colors disabled:opacity-50">🗑️ Delete</button>
      </div>
    </div>
  );
}
